<template>
    <div class="flex w-0 flex-1 flex-col overflow-hidden">
        <ToggleSidebar />

        <main
            class="focus:outline-none relative z-0 flex-1 overflow-y-auto"
            tabindex="0"
        >
            <div class="pb-6 pt-2 md:py-6">
                <div class="mx-auto px-4 sm:px-6 lg:px-8">
                    <div class="md:flex md:items-center md:justify-between">
                        <div class="min-w-0 flex-1 flex items-center">
                            <BasePageTitle title="Content" />
                        </div>
                        <BaseSpinner :isLoading="isLoading" />
                    </div>
                </div>


                <div class="mx-auto px-0 py-4 sm:px-6 md:px-8">

                  <!-- Stories Section -->
                  <div
                      v-if="isFeatureAvailable(FeaturesEnum.INTERNAL) || isFeatureAvailable(FeaturesEnum.STORIES)"
                      class="rounded bg-white shadow-md sm:rounded-lg p-3 overflow-visible">
                    <div class="flex flex-col justify-between px-2 py-2.5 sm:flex-row mb-4">
                      <ul class="mb-2 flex flex-row items-center sm:mb-0 overflow-scroll">
                        <li
                            @click="updateStoriesFilter('all')"
                            class="mr-1 cursor-pointer rounded-md px-3 py-1 text-xs transition duration-150 ease-in-out hover:bg-gray-50"
                            :class="{
                                        'bg-gray-100': activeStoriesFilter === 'all'
                                    }"
                        >
                          All
                        </li>
                        <li
                            @click="updateStoriesFilter(true)"
                            class="mr-1 cursor-pointer rounded-md px-3 py-1 text-xs transition duration-150 ease-in-out hover:bg-gray-50"
                            :class="{
                                        'bg-gray-100': activeStoriesFilter === true
                                    }"
                        >
                          Active
                        </li>
                        <li
                            @click="updateStoriesFilter(false)"
                            class="mr-1 cursor-pointer rounded-md px-3 py-1 text-xs transition duration-150 ease-in-out hover:bg-gray-50"
                            :class="{
                                        'bg-gray-100': activeStoriesFilter === false
                                    }"
                        >
                          Inactive
                        </li>
                      </ul>
                      <div class="flex justify-end">
                        <base-button
                            size="sm"
                            button-text="Create Story"
                            @clicked="createStory"
                            button-style="action"
                        />
                      </div>
                    </div>

                    <story-grid
                        :stories="stories"
                        :is-loading="isStoriesLoading"
                    />
                  </div>

                    <div class="rounded bg-white shadow-md sm:rounded-lg mt-6">
                        <div
                            class="flex flex-col justify-between px-2 py-2.5 sm:flex-row sm:px-6 mb-4"
                        >
                            <ul class="mb-2 flex flex-row items-center sm:mb-0 overflow-scroll">
                                <li
                                    @click="
                                        updateParams({
                                            status: filter.value,
                                            page: 1
                                        })
                                    "
                                    class="mr-1 cursor-pointer rounded-md px-3 py-1 text-xs transition duration-150 ease-in-out hover:bg-gray-50"
                                    :class="{
                                        'bg-gray-100':
                                            filter.value === serverParams.status
                                    }"
                                    v-for="filter in offersFilters"
                                    :key="filter.value"
                                >
                                    {{ filter.label }}
                                </li>
                            </ul>
                            <div class="flex">
                                <search-input
                                    class="text-gray-700 sm:mr-3"
                                    v-model="searchString"
                                    :min="3"
                                    :max="9"
                                    size="sm"
                                    placeholder="Search by name"
                                />

                                <venue-selector
                                    :venues="venues"
                                    :multiple-select="true"
                                    :allow-empty="true"
                                    :key="venues ? venues.length : 0"
                                    size="sm"
                                    @selected="onVenueChange"
                                />

                                <base-button
                                    size="sm"
                                    button-text="Create Banner"
                                    @clicked="$router.push({ name: 'newOffer' })"
                                    class="ml-3"
                                    button-style="action"
                                />
                              </div>
                        </div>
                        <banner-grid
                            :banners="offers"
                            :is-loading="isLoading"
                        />
                        
                        <div v-if="!isLoading && totalRecords > 0" class="px-6 py-4 border-t border-gray-200">
                            <div class="flex items-center justify-end">

                                <div class="flex items-center space-x-2">

                                    <button
                                        @click="onPageChange(serverParams.page - 1)"
                                        :disabled="serverParams.page === 1"
                                        class="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Previous
                                    </button>
                                    <span class="text-sm text-gray-700">
                                        Page {{ serverParams.page }} of {{ Math.ceil(totalRecords / serverParams.limit) }}
                                    </span>
                                    <button
                                        @click="onPageChange(serverParams.page + 1)"
                                        :disabled="serverParams.page >= Math.ceil(totalRecords / serverParams.limit)"
                                        class="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>


                </div>
            </div>
        </main>
    </div>
</template>

<script>
import ToggleSidebar from '../components/common/ToggleSidebar';
import VenueSelector from '../components/selectors/VenueSelector';
import { mapActions, mapGetters } from 'vuex';
import SearchInput from '@/components/widgets/SearchInput';
import BannerGrid from '../components/BannerGrid';
import StoryGrid from '../components/StoryGrid';
import { FeaturesEnum } from "@/enums";

export default {
    name: 'OffersList',
    metaInfo: {
        title: 'Content'
    },
    components: {
        BannerGrid,
        SearchInput,
        ToggleSidebar,
        VenueSelector,
        StoryGrid
    },
    data() {
        return {
            offers: [],
            FeaturesEnum,
            isLoading: true,
            offersFilters: [
                {
                    label: 'All',
                    value: 'all'
                },
                {
                    label: 'Active',
                    value: 'active'
                },
                {
                    label: 'Scheduled',
                    value: 'scheduled'
                },
                {
                    label: 'Expired',
                    value: 'expired'
                },
                {
                    label: 'Archived',
                    value: 'archived'
                }
            ],
            filter: 'All',
            venueFilter: '',
            searchString: '',
            totalRecords: 0,
            serverParams: {
                page: 1,
                limit: 20,
                sortBy: '',
                sortOrder: '',
                venueIds: null,
                status: 'all',
                q: ''
            },
            stories: [],
            isStoriesLoading: true,
            activeStoriesFilter: 'all'
        };
    },
    computed: {
        ...mapGetters({
            isFeatureAvailable: 'user/isFeatureAvailable',
            venues: 'venues/getVenues'
        })
    },
    async created() {
        if (!this.venues?.length > 0) {
            await this.fetchVenues();
        }
        await this.fetchOffers();
        await this.fetchStories();
    },
    watch: {
        searchString(value) {
            const searchString = value.toLowerCase();

            if (this.timer) {
                clearTimeout(this.timer);
                this.timer = null;
            }

            this.timer = setTimeout(() => {
                const searchValueConditions =
                    searchString.length >= 3 &&
                    searchString.length <= 30 &&
                    this.serverParams.q !== searchString;
                const clearSearchResults = !searchString && this.serverParams.q;

                if (searchValueConditions || clearSearchResults) {
                    this.updateParams({ q: searchString });
                }
            }, 600);
        }
    },
    methods: {
        ...mapActions({
            fetchVenues: 'venues/fetch'
        }),
        async fetchOffers() {
            this.isLoading = true;
            try {
                const {
                    data: { results, total }
                } = await this.$axios.get('/offers', {
                    params: this.serverParams
                });

                this.totalRecords = total;
                this.offers = results;
            } catch (error) {
                throw new Error(`API ${error}`);
            } finally {
                this.isLoading = false;
            }
        },
        updateParams(newProps) {
            this.serverParams = Object.assign({}, this.serverParams, newProps);
            this.fetchOffers();
        },
        onPageChange(page) {
            this.updateParams({ page });
        },
        onPerPageChange() {
            this.updateParams({ page: 1 });
        },
        onVenueChange(venueIds) {
            this.updateParams({ venueIds, page: 1 });
            this.venueFilter = venueIds;
        },
        async fetchStories() {
            this.isStoriesLoading = true;
            try {
                const params = {};
                if (this.activeStoriesFilter !== 'all') {
                    params.active = this.activeStoriesFilter;
                }

                const { data } = await this.$axios.get('/stories', { params });
                this.stories = data;
            } catch {
                this.$notify({
                    group: 'settings',
                    type: 'error',
                    text: 'Failed to load stories'
                });
            } finally {
                this.isStoriesLoading = false;
            }
        },
        updateStoriesFilter(filter) {
            this.activeStoriesFilter = filter;
            this.fetchStories();
        },
        createStory() {
            this.$router.push({ name: 'createStory' });
        }
    }
};
</script>
